import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Tag, AlertTriangle, RotateCcw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useCreateProduct, useUpdateProduct } from '@/hooks/useProducts';
import { SkuConflictError } from '@/lib/api/products';
import { useAuth } from '@/hooks/useAuth';
import { useCategories, useCreateCategory } from '@/hooks/useCategories';
import { ProductImageField } from '@/components/inventory/ProductImageField';
import { removeStoredImage, uploadProductImage } from '@/lib/productImages';
import type { Product } from '@/types/domain';

export interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  product?: Product | null;
}

interface FormValues {
  sku: string;
  name: string;
  price: number;
  costPrice?: number;
  categoryId?: string;
  description?: string;
  brand?: string;
  unit?: string;
}

export function ProductForm({ open, onClose, product }: ProductFormProps) {
  const { user } = useAuth();
  const isEdit = !!product;
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const { data: categoryDocs } = useCategories({ isActive: true });
  const createCategory = useCreateCategory();

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  // Save failures are shown here, in the form. They used to be swallowed:
  // the mutation rejected, react-hook-form dropped the rejection, and the
  // dialog just sat there — which read as "the button does nothing".
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Product | null>(null);
  // The image is uploaded on save, not on pick, so a cancelled dialog leaves
  // nothing behind in storage.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      sku: '',
      name: '',
      price: 0,
      costPrice: undefined,
      categoryId: '',
      description: '',
      brand: '',
      unit: '',
    },
  });

  const categoryId = watch('categoryId');

  useEffect(() => {
    if (open) {
      if (product) {
        reset({
          sku: product.sku,
          name: product.name,
          price: product.price,
          costPrice: product.costPrice,
          categoryId: product.categoryId ?? '',
          description: product.description ?? '',
          brand: product.brand ?? '',
          unit: product.unit ?? '',
        });
      } else {
        reset({
          sku: '',
          name: '',
          price: 0,
          costPrice: undefined,
          categoryId: '',
          description: '',
          brand: '',
          unit: '',
        });
      }
      setShowAddCategory(false);
      setNewCategoryName('');
      setSubmitError(null);
      setConflict(null);
      setImageFile(null);
      setImageRemoved(false);
    }
  }, [open, product, reset]);

  const onSubmit = async (values: FormValues) => {
    if (!user) return;
    setSubmitError(null);
    setConflict(null);
    try {
      // Normalize empty string to undefined so Firestore stores `null`
      // (or the field is absent for legacy).
      const catId = values.categoryId && values.categoryId.trim()
        ? values.categoryId.trim()
        : undefined;

      // undefined = leave the image as it is; null = remove it.
      let imageUrl: string | null | undefined;
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await uploadProductImage(imageFile, product?.id ?? values.sku);
        } finally {
          setUploading(false);
        }
      } else if (imageRemoved) {
        imageUrl = null;
      }
      if (isEdit && product) {
        await update.mutateAsync({
          id: product.id,
          input: {
            sku: values.sku.trim(),
            name: values.name.trim(),
            price: Number(values.price),
            costPrice: values.costPrice ? Number(values.costPrice) : undefined,
            categoryId: catId,
            description: values.description?.trim() || undefined,
            // Brand is only sent when there is something to write or clear, so
            // an edit that never touches it does not depend on migration 0042.
            brand: values.brand?.trim() || (product.brand ? null : undefined),
            unit: values.unit?.trim() || 'pcs',
            ...(imageUrl !== undefined ? { imageUrl } : {}),
            updatedBy: user.uid,
            updatedByName: user.displayName ?? null,
            updatedByEmail: user.email ?? null,
          },
        });
      } else {
        await create.mutateAsync({
          sku: values.sku.trim(),
          name: values.name.trim(),
          price: Number(values.price),
          costPrice: values.costPrice ? Number(values.costPrice) : undefined,
          categoryId: catId,
          description: values.description?.trim() || undefined,
          brand: values.brand?.trim() || undefined,
          unit: values.unit?.trim() || undefined,
          ...(imageUrl ? { imageUrl } : {}),
          createdBy: user.uid,
          createdByName: user.displayName ?? null,
          createdByEmail: user.email ?? null,
        });
      }
      // The old file is only removed once the product points at the new one.
      if (isEdit && imageUrl !== undefined && product?.imageUrl) {
        void removeStoredImage(product.imageUrl);
      }
      onClose();
    } catch (e) {
      if (e instanceof SkuConflictError) setConflict(e.product);
      setSubmitError(e instanceof Error ? e.message : 'Gagal menyimpan produk');
    }
  };

  /** The SKU belongs to a deactivated product: bring that one back instead. */
  const reactivateConflict = async () => {
    if (!conflict || !user) return;
    try {
      await update.mutateAsync({
        id: conflict.id,
        input: {
          isActive: true,
          updatedBy: user.uid,
          updatedByName: user.displayName ?? null,
          updatedByEmail: user.email ?? null,
        },
      });
      onClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Gagal mengaktifkan produk');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !user) return;
    try {
      const newId = await createCategory.mutateAsync({
        name: newCategoryName.trim(),
        createdBy: user.uid,
      });
      setValue('categoryId', newId);
      setShowAddCategory(false);
      setNewCategoryName('');
    } catch {
      // Error already shown by the mutation hook
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit product' : 'New product'}
      description={
        isEdit ? 'Update product information.' : 'Tambah produk baru ke inventory.'
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="product-form"
            variant="primary"
            loading={isSubmitting || uploading || create.isPending || update.isPending}
          >
            {isEdit ? 'Save changes' : 'Create product'}
          </Button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <ProductImageField
          currentUrl={product?.imageUrl}
          file={imageFile}
          removed={imageRemoved}
          onPick={(f) => { setImageFile(f); setImageRemoved(false); }}
          onRemove={() => { setImageFile(null); setImageRemoved(true); }}
          disabled={isSubmitting || uploading}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="SKU"
            placeholder="PRD-001"
            error={errors.sku?.message}
            {...register('sku', { required: 'SKU wajib diisi' })}
          />
          <Input
            label="Product name"
            placeholder="Contoh: Kaos Polos Hitam M"
            error={errors.name?.message}
            {...register('name', { required: 'Nama wajib diisi' })}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Brand" placeholder="Opsional" {...register('brand')} />
          <Input label="Unit" placeholder="pcs" {...register('unit')} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Price (IDR)"
            type="number"
            min={0}
            step="any"
            error={errors.price?.message}
            {...register('price', {
              required: 'Harga wajib diisi',
              valueAsNumber: true,
              validate: (v) => v >= 0 || 'Harga tidak boleh negatif',
            })}
          />
          <Input
            label="Cost price (opsional)"
            type="number"
            min={0}
            step="any"
            hint="Modal / HPP. Untuk laporan profit."
            {...register('costPrice', { valueAsNumber: true })}
          />
        </div>

        {/* Category dropdown — global categories managed from the
            Inventory → Kategori tab. The "+ Buat baru" affordance lets
            the owner add a category inline without leaving the form. */}
        <div className="space-y-1.5">
          <label
            htmlFor="product-category"
            className="block text-small font-medium text-ink"
          >
            Kategori
          </label>
          {!showAddCategory ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  id="product-category"
                  value={categoryId ?? ''}
                  onChange={(value) => setValue('categoryId', value, { shouldDirty: true })}
                >
                  <option value="">— Tanpa kategori —</option>
                  {(categoryDocs ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => setShowAddCategory(true)}
                leftIcon={<Plus size={14} />}
                title="Buat kategori baru (langsung)"
              >
                <span className="hidden sm:inline">Baru</span>
              </Button>
            </div>
          ) : (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nama kategori baru"
                  maxLength={40}
                  autoFocus
                  leftIcon={<Tag size={14} />}
                />
              </div>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={handleAddCategory}
                loading={createCategory.isPending}
                disabled={!newCategoryName.trim()}
              >
                Simpan
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => {
                  setShowAddCategory(false);
                  setNewCategoryName('');
                }}
                disabled={createCategory.isPending}
              >
                Batal
              </Button>
            </div>
          )}
          {showAddCategory && (
            <p className="text-caption text-ink-subtle">
              Kategori akan dipakai di semua event. Bisa di-rename nanti.
            </p>
          )}
        </div>

        <div className="p-3 rounded bg-canvas border border-line">
          <p className="text-caption text-ink-subtle">
            Stock di-track per-event, bukan global. Setelah produk dibuat, set alokasi
            qty-nya dari halaman <strong>Events → Manage Stock</strong>.
          </p>
        </div>

        <Textarea
          label="Description (opsional)"
          placeholder="Catatan produk, warna, ukuran, dll."
          rows={3}
          {...register('description')}
        />
      </form>

      {submitError && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 p-3 rounded border border-danger/30 bg-danger/5 text-small text-ink"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-danger" />
          <div className="space-y-2">
            <p>{submitError}</p>
            {conflict && !conflict.isActive && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={<RotateCcw size={14} />}
                loading={update.isPending}
                onClick={reactivateConflict}
              >
                Aktifkan kembali &quot;{conflict.name}&quot;
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
